// RGM Starter Studio — basic mastering (playback-only master bus).
// EQ → glue compressor → makeup gain → safety limiter. Intensity 0–100
// scales every move from neutral, so 0 ≈ bypass and 100 = full preset.
export const MASTER_PRESETS = [
  { key: "off", label: "Off", hint: "No mastering" },
  { key: "clean", label: "Clean", hint: "Gentle polish, natural dynamics" },
  { key: "loud", label: "Loud", hint: "Punchy and louder" },
  { key: "clarity", label: "Clarity", hint: "Brighter vocals, less mud" },
  { key: "warm", label: "Warm", hint: "Fuller lows, softer highs" },
];

const SPECS = {
  off: { low: 0, mid: 0, high: 0, thr: 0, ratio: 1, makeup: 0 },
  clean: { low: 0.5, mid: 0, high: 1, thr: -16, ratio: 2, makeup: 1.5 },
  loud: { low: 1.5, mid: 0, high: 1.5, thr: -26, ratio: 4, makeup: 6 },
  clarity: { low: -1, mid: -2.5, high: 4, thr: -18, ratio: 2.5, makeup: 2 },
  warm: { low: 4, mid: 0.5, high: -2, thr: -18, ratio: 2.5, makeup: 2 },
};

export const masterParams = (preset = "off", intensity = 50) => {
  const s = SPECS[preset] || SPECS.off;
  const k = Math.max(0, Math.min(100, Number(intensity) || 0)) / 100;
  return {
    lowDb: s.low * k,
    midDb: s.mid * k,
    highDb: s.high * k,
    threshold: s.thr * k || 0,
    ratio: 1 + (s.ratio - 1) * k,
    makeupGain: Math.pow(10, (s.makeup * k) / 20),
    limiter: preset !== "off" && k > 0,
  };
};

export function buildMasterChain(ctx) {
  const input = ctx.createGain();
  const low = ctx.createBiquadFilter();
  low.type = "lowshelf"; low.frequency.value = 160;
  const mid = ctx.createBiquadFilter();
  mid.type = "peaking"; mid.frequency.value = 350; mid.Q.value = 0.9;
  const high = ctx.createBiquadFilter();
  high.type = "highshelf"; high.frequency.value = 8000;
  const comp = ctx.createDynamicsCompressor();
  comp.knee.value = 8; comp.attack.value = 0.01; comp.release.value = 0.25;
  const makeup = ctx.createGain();
  const limiter = ctx.createDynamicsCompressor();
  limiter.knee.value = 0; limiter.attack.value = 0.002; limiter.release.value = 0.1; limiter.ratio.value = 20;
  // "Off" is a true bypass: the dry path skips EQ + dynamics entirely.
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  // Post-master tap for the output meter (reads the real signal).
  const out = ctx.createGain();
  out.connect(ctx.destination);
  const analyser = ctx.createAnalyser ? ctx.createAnalyser() : null;
  if (analyser) { analyser.fftSize = 2048; out.connect(analyser); }
  input.connect(dry).connect(out);
  input.connect(low).connect(mid).connect(high).connect(comp).connect(makeup).connect(limiter).connect(wet).connect(out);

  const apply = (preset, intensity) => {
    const p = masterParams(preset, intensity);
    const t = ctx.currentTime;
    low.gain.setTargetAtTime(p.lowDb, t, 0.05);
    mid.gain.setTargetAtTime(p.midDb, t, 0.05);
    high.gain.setTargetAtTime(p.highDb, t, 0.05);
    comp.threshold.setTargetAtTime(p.threshold, t, 0.05);
    comp.ratio.setTargetAtTime(p.ratio, t, 0.05);
    makeup.gain.setTargetAtTime(p.makeupGain, t, 0.05);
    limiter.threshold.setTargetAtTime(p.limiter ? -1 : 0, t, 0.05);
    dry.gain.setTargetAtTime(p.limiter ? 0 : 1, t, 0.03);
    wet.gain.setTargetAtTime(p.limiter ? 1 : 0, t, 0.03);
  };
  dry.gain.value = 1;
  wet.gain.value = 0;
  apply("off", 0);
  const setVolume = (v) => out.gain.setTargetAtTime(Math.max(0, Math.min(1.5, v)), ctx.currentTime, 0.02);
  return { input, apply, analyser, setVolume };
}
